import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Package,
  Plus,
  Search,
  RefreshCw,
  Trash2,
  ExternalLink,
  Target,
  Users,
  Filter,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useOutletContext } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatPrice(price) {
  if (price === null || price === undefined) return "N/A";
  return `₹${price.toLocaleString("en-IN")}`;
}

function PriceChange({ value, className }) {
  if (value === null || value === undefined) {
    return <span className={cn("text-[#8a8278]", className)}>—</span>;
  }

  if (value < 0) {
    return (
      <span className={cn("text-[#1a7a3c] font-semibold", className)}>
        ▼ {Math.abs(value).toFixed(1)}%
      </span>
    );
  }

  if (value > 0) {
    return (
      <span className={cn("text-[#c0392b] font-semibold", className)}>
        ▲ {value.toFixed(1)}%
      </span>
    );
  }

  return <span className={cn("text-[#8a8278]", className)}>0%</span>;
}

export default function Products() {
  const { isAdmin } = useOutletContext() || {};
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState({});

  // Form state
  const [newProduct, setNewProduct] = useState({
    asin: "",
    product_type: "own",
    name: "",
    category: "",
    brand: "",
    specs: "",
    bau_price: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.asin || newProduct.asin.length !== 10) {
      toast.error("Please enter a valid 10-character ASIN");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...newProduct,
        bau_price: newProduct.bau_price ? parseFloat(newProduct.bau_price) : null,
      };
      const token = localStorage.getItem('pricelens_token');
      const response = await axios.post(`${API}/products`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setProducts([...products, response.data]);
      toast.success("Product added — fetching live Keepa prices in background...");
      setAddDialogOpen(false);
      setNewProduct({ asin: "", product_type: "own", name: "", category: "", brand: "", specs: "", bau_price: "", notes: "" });

      // Server auto-syncs in background; refresh list to pick up prices
      setTimeout(fetchProducts, 15000);
      setTimeout(fetchProducts, 45000);
    } catch (error) {
      console.error("Error adding product:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error(error.response?.data?.detail || "Failed to add product");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const syncProduct = async (productId) => {
    setSyncing((prev) => ({ ...prev, [productId]: true }));
    try {
      const token = localStorage.getItem('pricelens_token');
      const response = await axios.post(`${API}/sync/${productId}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? response.data : p))
      );
      toast.success("Product synced");
    } catch (error) {
      console.error("Error syncing product:", error);
      toast.error("Failed to sync product");
    } finally {
      setSyncing((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const deleteProduct = async (productId) => {
    try {
      const token = localStorage.getItem('pricelens_token');
      await axios.delete(`${API}/products/${productId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setProducts((prev) => prev.filter((p) => p.id !== productId));
      toast.success("Product deleted");
    } catch (error) {
      console.error("Error deleting product:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to delete product");
      }
    }
  };

  // Filter products
  const filteredProducts = products.filter((p) => {
    const matchesSearch =
      (p.title?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (p.name?.toLowerCase() || "").includes(search.toLowerCase()) ||
      p.asin.toLowerCase().includes(search.toLowerCase());

    const matchesFilter =
      filter === "all" || p.product_type === filter;

    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="products-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1814]">
            Products
          </h1>
          <p className="text-sm text-[#8a8278]">
            Manage your tracked ASINs and competition
          </p>
        </div>

        {/* Add Product Dialog - Admin Only */}
        {isAdmin && (
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-product-dialog-btn"
                className="bg-[#f0a500] hover:bg-[#ffc533] text-[#1a1200] font-bold w-fit"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-white border-[#e0dbd2]">
            <DialogHeader>
              <DialogTitle className="text-lg font-extrabold">
                Add New Product
              </DialogTitle>
              <DialogDescription>
                Enter the Amazon ASIN to start tracking price
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleAddProduct} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="asin">ASIN *</Label>
                  <Input
                    id="asin"
                    data-testid="asin-input"
                    placeholder="e.g., B0BSHF7WHW"
                    value={newProduct.asin}
                    onChange={(e) =>
                      setNewProduct({
                        ...newProduct,
                        asin: e.target.value.toUpperCase(),
                      })
                    }
                    maxLength={10}
                    className="bg-[#f2f0ec] border-[#e0dbd2] font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Product Type *</Label>
                  <Select
                    value={newProduct.product_type}
                    onValueChange={(value) =>
                      setNewProduct({ ...newProduct, product_type: value })
                    }
                  >
                    <SelectTrigger
                      data-testid="product-type-select"
                      className="bg-[#f2f0ec] border-[#e0dbd2]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#e0dbd2]">
                      <SelectItem value="own">
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-blue-600" />
                          My Product
                        </div>
                      </SelectItem>
                      <SelectItem value="competition">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-amber-500" />
                          Competition
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Product Name</Label>
                  <Input
                    id="name"
                    data-testid="product-name-input"
                    placeholder="e.g., Nutri-Blend Pro 750W"
                    value={newProduct.name}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, name: e.target.value })
                    }
                    className="bg-[#f2f0ec] border-[#e0dbd2]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="brand">Brand</Label>
                  <Input
                    id="brand"
                    placeholder="e.g., Philips"
                    value={newProduct.brand}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, brand: e.target.value })
                    }
                    className="bg-[#f2f0ec] border-[#e0dbd2]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    placeholder="e.g., Mixer Grinder"
                    value={newProduct.category}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, category: e.target.value })
                    }
                    className="bg-[#f2f0ec] border-[#e0dbd2]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bau_price">BAU Price (₹)</Label>
                  <Input
                    id="bau_price"
                    type="number"
                    placeholder="e.g., 3500"
                    value={newProduct.bau_price}
                    onChange={(e) =>
                      setNewProduct({ ...newProduct, bau_price: e.target.value })
                    }
                    className="bg-[#f2f0ec] border-[#e0dbd2] font-mono"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="specs">Specs</Label>
                <Input
                  id="specs"
                  placeholder="e.g., 750W | 3 Jars | 5 Speed"
                  value={newProduct.specs}
                  onChange={(e) =>
                    setNewProduct({ ...newProduct, specs: e.target.value })
                  }
                  className="bg-[#f2f0ec] border-[#e0dbd2]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  data-testid="product-notes-input"
                  placeholder="Any notes about this product..."
                  value={newProduct.notes}
                  onChange={(e) =>
                    setNewProduct({ ...newProduct, notes: e.target.value })
                  }
                  className="bg-[#f2f0ec] border-[#e0dbd2] resize-none"
                  rows={2}
                />
              </div>

              <DialogFooter className="gap-2">
                <DialogClose asChild>
                  <Button variant="outline" type="button" className="border-[#e0dbd2]">
                    Cancel
                  </Button>
                </DialogClose>
                <Button
                  type="submit"
                  data-testid="submit-product-btn"
                  disabled={submitting || newProduct.asin.length !== 10}
                  className="bg-[#1a1814] hover:bg-[#333] text-white font-bold"
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    "Add Product"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#8a8278]" />
          <Input
            data-testid="search-input"
            placeholder="Search by name or ASIN..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-[#e0dbd2]"
          />
        </div>

        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger
            data-testid="filter-select"
            className="w-[180px] bg-white border-[#e0dbd2]"
          >
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-[#e0dbd2]">
            <SelectItem value="all">All Products</SelectItem>
            <SelectItem value="own">My Products</SelectItem>
            <SelectItem value="competition">Competition</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Products List */}
      {filteredProducts.length === 0 ? (
        <Card className="bg-white border-[#e0dbd2]">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Package className="h-16 w-16 text-[#8a8278] mb-4" />
            <h3 className="text-lg font-semibold mb-2">No products found</h3>
            <p className="text-[#8a8278] text-center max-w-sm">
              {search || filter !== "all"
                ? "Try adjusting your search or filters"
                : "Add your first product ASIN to start tracking prices"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredProducts.map((product) => {
            const isBelowBAU = product.bau_price && product.current_price && product.current_price < product.bau_price;
            
            return (
              <Card
                key={product.id}
                data-testid={`product-card-${product.asin}`}
                className={cn(
                  "bg-white border-[#e0dbd2] overflow-hidden transition-all hover:shadow-md",
                  isBelowBAU && "border-[#c0392b]/30 bg-[#c0392b]/5"
                )}
              >
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row md:items-center gap-4 p-4">
                    {/* Image */}
                    <Link
                      to={`/products/${product.id}`}
                      className="flex-shrink-0"
                    >
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.title}
                          className="w-16 h-16 rounded-lg object-cover bg-[#f2f0ec]"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-[#f2f0ec] flex items-center justify-center text-2xl">
                          {product.product_type === 'own' ? '📦' : '🎯'}
                        </div>
                      )}
                    </Link>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 mb-1">
                        <Link
                          to={`/products/${product.id}`}
                          className="font-semibold text-[#1a1814] hover:text-[#f0a500] transition-colors truncate"
                        >
                          {product.title || product.name || "Unnamed Product"}
                        </Link>
                        <Badge
                          className={cn(
                            "flex-shrink-0 text-[10px]",
                            product.product_type === "own"
                              ? "bg-blue-100 text-blue-700 border-blue-200"
                              : "bg-amber-100 text-amber-700 border-amber-200"
                          )}
                        >
                          {product.product_type === "own" ? "Own" : "Competition"}
                        </Badge>
                        {isBelowBAU && (
                          <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">
                            Below BAU
                          </Badge>
                        )}
                      </div>

                      <p className="text-xs text-[#8a8278] font-mono mb-2">
                        {product.asin}
                        {product.brand && ` · ${product.brand}`}
                        {product.category && ` · ${product.category}`}
                      </p>

                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div>
                          <span className="text-[#8a8278]">Price: </span>
                          <span className="font-bold font-mono text-[#1a1814]">
                            {formatPrice(product.current_price)}
                          </span>
                        </div>
                        {product.bau_price && (
                          <div>
                            <span className="text-[#8a8278]">BAU: </span>
                            <span className="font-mono text-[#8a8278]">
                              {formatPrice(product.bau_price)}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="text-[#8a8278]">24h: </span>
                          <PriceChange value={product.price_change_24h} />
                        </div>
                        <div>
                          <span className="text-[#8a8278]">7d: </span>
                          <PriceChange value={product.price_change_7d} />
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`sync-btn-${product.asin}`}
                          onClick={() => syncProduct(product.id)}
                          disabled={syncing[product.id]}
                          className="border-[#e0dbd2] hover:bg-[#f2f0ec]"
                        >
                          <RefreshCw
                            className={cn(
                              "h-4 w-4",
                              syncing[product.id] && "animate-spin"
                            )}
                          />
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="border-[#e0dbd2] hover:bg-[#f2f0ec]"
                      >
                        <a
                          href={`https://www.amazon.in/dp/${product.asin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`amazon-link-${product.asin}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>

                      {/* Delete Button - Admin Only */}
                      {isAdmin && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid={`delete-btn-${product.asin}`}
                              className="border-[#e0dbd2] hover:bg-red-50 hover:text-[#c0392b] hover:border-red-200"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-white border-[#e0dbd2]">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Product</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete {product.title || product.asin}? This will also delete all price history.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-[#e0dbd2]">
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteProduct(product.id)}
                                className="bg-[#c0392b] hover:bg-[#a93226] text-white"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
